import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-accent text-on-accent font-semibold hover:brightness-[0.92] active:brightness-[0.88]',
  secondary:
    'bg-transparent text-text-1 shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-1',
  ghost: 'text-text-1 hover:bg-surface-2',
  // Ações destrutivas em contorno: o vermelho de erro não compete com a cor de ação do produto.
  danger:
    'bg-transparent text-critical shadow-[inset_0_0_0_1px_var(--critical)] hover:bg-critical-soft',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm gap-1.5',
  md: 'h-11 px-5 text-[15px] gap-2',
  lg: 'h-14 px-7 text-[17px] gap-2.5',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  fullWidth,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        // Botões em pílula, como na marca Nolens.
        'inline-flex items-center justify-center rounded-full font-medium whitespace-nowrap transition duration-150 ease-nolens',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : icon}
      {children}
    </button>
  );
}
